import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  inject,
} from '@angular/core';
import { ClassicPreset } from 'rete';
import { ImpureKeyvaluePipe, RefDirective } from 'rete-angular-plugin/21';
import { MouldingNodeContentComponent } from '../moulding-node-content/moulding-node-content.component';

type NodeData = ClassicPreset.Node & { width?: number; height?: number };

/**
 * Stable Rete node shell. Keep its template and lifecycle limited to Rete
 * rendering so Angular can hot-replace the projected node UI independently.
 */
@Component({
  selector: 'app-moulding-node',
  standalone: true,
  imports: [RefDirective, ImpureKeyvaluePipe, MouldingNodeContentComponent],
  templateUrl: './moulding-node.component.html',
})
export class MouldingNodeComponent implements OnChanges, OnDestroy {
  // These inputs are set by the Rete Angular renderer, not a normal Angular
  // parent component. `emit` lets projected socket/control hosts signal Rete.
  @Input() data!: NodeData;
  @Input() emit!: (data: unknown) => void;
  @Input() rendered!: () => void;

  // Changing this value causes the projected refComponent hosts to emit their
  // render events again, keeping Rete socket/control instances in sync.
  seed = 0;

  private readonly cdr = inject(ChangeDetectorRef);
  private renderedFrame: number | null = null;

  constructor() {
    // Rete drives updates through ngOnChanges. Leaving this view attached to
    // Angular's normal scheduler causes a Rete render/change-detection loop.
    this.cdr.detach();
  }

  ngOnChanges(): void {
    // Update projected Rete hosts before asking Rete to re-measure this node.
    this.seed++;
    this.cdr.detectChanges();
    this.scheduleRendered();
  }

  sortByIndex(
    a: { key: string; value?: { index?: number } },
    b: { key: string; value?: { index?: number } },
  ): number {
    const ai = a.value?.index || 0;
    const bi = b.value?.index || 0;
    return ai - bi;
  }

  scheduleRendered(): void {
    // The content child emits layoutChanged after rename mode changes its size.
    // Coalescing to one frame prevents redundant Rete resize operations.
    if (this.renderedFrame !== null) {
      cancelAnimationFrame(this.renderedFrame);
    }
    this.renderedFrame = requestAnimationFrame(() => {
      this.renderedFrame = null;
      this.rendered();
    });
  }

  ngOnDestroy(): void {
    // Do not notify a destroyed Rete node after a pending animation frame.
    if (this.renderedFrame !== null) {
      cancelAnimationFrame(this.renderedFrame);
    }
  }
}
